import { clsx } from 'clsx';
import { Children, isValidElement } from 'react';
import { twMerge } from 'tailwind-merge';

import type config from '../tailwind.config.js';
import type { ClassValue } from 'clsx';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const MOBILE_BREAKPOINT = 640;

// Convert Tailwind color to HSL value
type Colors = typeof config.theme extends { colors: infer C } ? C : never;
type ColorKeys<T> = T extends object
  ? keyof T extends string
    ? keyof T | `${keyof T}-${ColorKeys<T[keyof T]>}`
    : never
  : never;

export type ColorValue = ColorKeys<Colors>;

export const getColorValue = (color: string): string => {
  if (color.includes('-')) {
    const [base, variant] = color.split('-');
    return `hsl(var(--${base}-${variant}))`;
  }

  return `hsl(var(--${color}))`;
};

// Check if the item should be a mobile view based on width
// Shared between Clients and Sessions
export const isMobileByWidth = (width?: number | null): boolean => {
  return width ? width <= MOBILE_BREAKPOINT : false;
};

// Format seconds to 00:00 eg. 350 -> 05:50
// If format is 'labels', return 5m 50s
export const secondsToTime = (
  value: number, // seconds
  format?: 'default' | 'labels',
  precision: 'second' | 'decisecond' = 'second',
): string => {
  // Handle negative values and ensure value is <100 hours
  const clampedValue = Math.max(0, Math.min(value, 359999));
  const secondsValue = precision === 'decisecond' ? Math.round(clampedValue * 10) / 10 : Math.round(clampedValue);

  const hours = Math.floor(secondsValue / 3600);
  const minutes = Math.floor((secondsValue % 3600) / 60);
  const remainingSeconds = secondsValue % 60;

  // Only the `labels` format leaves the whole seconds unpadded, and only
  // decisecond precision keeps a fraction.
  const getSecondsString = () => {
    const fraction = precision === 'decisecond' ? remainingSeconds % 1 : 0;
    const whole = Math.floor(remainingSeconds).toString();
    const wholeString = format === 'labels' ? whole : whole.padStart(2, '0');
    return fraction ? `${wholeString}.${Math.round(fraction * 10)}` : wholeString;
  };
  const secondsString = getSecondsString();

  // Format the final output
  if (format === 'labels') {
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secondsString}s`;
    }
    return minutes > 0 ? `${minutes}m ${secondsString}s` : `${secondsString}s`;
  }

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secondsString}`;
  }

  return `${minutes}:${secondsString}`;
};

export const getValidChildren = (children: React.ReactNode): React.ReactElement[] => {
  return Children.toArray(children).filter(child => isValidElement(child)) as React.ReactElement[];
};

// Pick the last room that has a composition, falling back to the last room if none do.
// This ensures we prefer rooms with actual recordings over empty ones.
type RoomWithComposition = { composition?: { duration?: number | null } | null };

export function findMostRelevantRoom<T extends RoomWithComposition>(
  rooms: (T | null | undefined)[] | null | undefined,
): T | undefined {
  const lastWithComposition = rooms?.findLast((room): room is T => room?.composition != null);
  return lastWithComposition ?? rooms?.at(-1) ?? undefined;
}

// Fisher-Yates shuffle — produces a uniformly random permutation, unlike
// [...arr].sort(() => Math.random() - 0.5) which is biased.
export function shuffleArray<T>(arr: T[]): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/*
 * Tone override for the two-tone illustrations in empty states and previews.
 *
 * The line takes --muted-foreground, the same value as the supporting copy beside
 * it, and the face takes --background. The shapes then read as outlined and
 * unfilled on the ground they land on. Both tokens resolve per theme, so this
 * needs no `dark:` variant.
 *
 * It overrides `--illustration-*`, which is what the illustrations read, and not
 * `--foreground` / `--background`.
 */
export const illustrationToneClass =
  '[--illustration-line:var(--muted-foreground)] [--illustration-face:var(--background)]';

/*
 * The two states of anything you pick by clicking it: a bordered row around a
 * checkbox or radio, and the illustration tiles in a pick-a-card row. Layout
 * (radius, padding, direction) stays with the call site. Only the state colours
 * are here, so every one of them shows selection in the same way.
 *
 * Idle takes the same faint wash as every other hoverable row, so a hover means
 * one thing everywhere. Selected takes the info block's tint, so a chosen option
 * reads as filled and not outlined. The tint replaces the edge instead of adding
 * to it.
 *
 * `RadioGroupCardItem` writes these as `data-[state=checked]:` variants. The label
 * pickers have no data-state, so they apply them conditionally.
 */
export const selectableOptionIdleClass =
  'hover:border-input hover:bg-foreground/[0.04] focus:border-input active:border-input';
export const selectableOptionSelectedClass = 'border-info-foreground bg-info-foreground';

/*
 * How a selected row or block shows its state: a fill, with no edge.
 *
 * The fill is a wash and not a fixed grey. The same row occurs on the card, the
 * canvas and the sidebar, and one opaque value cannot separate from all three.
 * A wash is measured from the surface below it, so it keeps its weight on each
 * one and inverts with the theme.
 *
 * A row that takes this fill takes no hover. Hover is the same wash one step
 * lighter, and a `:hover` rule wins against a plain class in either order, so the
 * two together make a selected row lighter under the pointer. That reads as a
 * deselection, so every call site guards its hover with `!isActive`.
 */
export const selectedBlockClass = 'bg-foreground/[0.065]';
